import { QueryReturnValue } from '@reduxjs/toolkit/dist/query/baseQueryTypes';
import axios from 'axios';
import { convertObjectToFormData } from '../../../utils/form';
import { BaseQueryError } from '../../types';
import { PresignedUrlParams, PresignedUrlResponse, S3UploadConfig, S3UploadResponse } from './types';

const SIGNING_URL = process.env.REACT_APP_AWS_S3_SIGNING_URL;
const UNKNOWN_USER_FOLDER = 'UNKNOWN';

const getPresignedURL = async (params: PresignedUrlParams): Promise<PresignedUrlResponse> => {
  if (!SIGNING_URL) throw new Error('Please add a signing url to upload a file');
  const { data } = await axios.post<PresignedUrlResponse>(SIGNING_URL, convertObjectToFormData(params));
  return data;
};

const sendFileToS3UsingPresignedUrl = async ({
  file,
  module,
  postEndpoint,
  signature,
  user,
}: {
  postEndpoint: string;
  module: string;
  user: string;
  file: File;
  signature: object;
}): Promise<S3UploadResponse> => {
  const data = convertObjectToFormData({
    ...signature,
    module,
    user,
    file,
  });
  const { data: responseData } = await axios.post<string>(postEndpoint, data);
  /**
   * Parses the XML response from S3 upload response and get the file object location
   */
  const xmlResponse = new window.DOMParser().parseFromString(responseData, 'text/xml');
  xmlResponse.children.item(0)?.children.item(0)?.innerHTML;
  const locationNode = xmlResponse.getElementsByTagName('Location')[0] as Element;
  const fileLink = locationNode.innerHTML;

  return {
    filename: file.name,
    filetype: file.type,
    link: fileLink,
    module,
  };
};

export const uploadToS3 = async <T extends File | null = File | null>({
  files,
  module,
  maxFilesize,
  user: _user,
}: S3UploadConfig<T>): Promise<(T extends File ? S3UploadResponse : S3UploadResponse | null)[]> => {
  const user = _user || UNKNOWN_USER_FOLDER;

  const signedUrls = await Promise.all(
    files.map((file) =>
      file
        ? getPresignedURL({
            module,
            user,
            contentType: file.type,
            filePath: file.name,
            maxFilesize,
          })
        : null
    )
  );

  const uploadResponses = await Promise.all(
    signedUrls.map((url, index) => {
      const file = files[index];
      if (url && file) {
        return sendFileToS3UsingPresignedUrl({
          file,
          module,
          postEndpoint: url.postEndpoint,
          signature: url.signature,
          user,
        });
      }
      return null;
    })
  );

  return uploadResponses as (T extends File ? S3UploadResponse : S3UploadResponse | null)[];
};

export const uploadToS3RtkApi = async <T extends File | null = File | null>(
  config: S3UploadConfig<T>
): Promise<QueryReturnValue<(T extends File ? S3UploadResponse : S3UploadResponse | null)[], BaseQueryError>> => {
  try {
    const uploadResponses = await uploadToS3(config);
    return {
      data: uploadResponses,
    };
  } catch (error) {
    const isAxiosError = axios.isAxiosError(error);
    return {
      error: {
        status: isAxiosError ? error.response?.status || 500 : 500,
        data: isAxiosError ? error.response?.data : error,
        message:
          isAxiosError && error.response?.data.message
            ? error.response.data.message
            : 'Could not upload your files. Please try again later.',
      },
    };
  }
};
